import {SagaIterator} from "redux-saga";
import {call, put, select, take} from "typed-redux-saga";
import {first, isNull} from "lodash";
import {
	getFanVoteHasChanges,
	getFanVoteHistory,
	getFanVotePlayerId,
	getProfile,
	isLoggedIn,
} from "modules/selectors";
import {
	addFanVotePlayer,
	continueFetchData,
	fetchFanVotes,
	fetchFanVotesFailed,
	fetchFanVotesSuccess,
	openDontLoseTeamModal,
	openFanVoteSavedModal,
	saveFanVotePlayerFailed,
	saveFanVotePlayerSuccess,
	setFanVoteHasChanged,
	toggleErrorModal,
} from "modules/actions";
import {Api, ApiError, ConnextraType, createConnextraScriptTag} from "modules/utils";
import {FAN_VOTE_KEY} from "modules/constants";
import i18next from "i18next";

export const fetchFanVotesSaga = function* ({
	payload: isNeedSetLocalVote,
}: ReturnType<typeof fetchFanVotes>): SagaIterator {
	try {
		const profile = yield* select(getProfile);
		const hasTerms = Boolean(profile?.terms);

		if (!hasTerms) {
			yield* put(fetchFanVotesFailed());
			yield* take(continueFetchData);
		}

		const response = yield* call(Api.Vote.getHistory);

		ApiError.CHECK(response);

		yield* put(fetchFanVotesSuccess(response.success));

		if (!isNeedSetLocalVote) {
			return;
		}

		// show un saved player
		yield* call(setUnSavedPlayerSaga);
	} catch (e) {
		yield* put(fetchFanVotesFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};

const setUnSavedPlayerSaga = function* () {
	const fanVotePlayer = JSON.parse(localStorage.getItem(FAN_VOTE_KEY) || "false") as number;

	if (fanVotePlayer) {
		yield* put(addFanVotePlayer(fanVotePlayer));
	}

	localStorage.removeItem(FAN_VOTE_KEY);
};

export const addFanVotePlayerSaga = function* () {
	const history = yield* select(getFanVoteHistory);
	const hasChanges = yield* select(getFanVoteHasChanges);

	if (!history.length && !hasChanges) {
		createConnextraScriptTag(ConnextraType.PICKS_START, null);
	}

	yield* put(setFanVoteHasChanged());
};

const trackAnalytics = function (playerId: number) {
	const season = Number(
		i18next.t("season", "", {
			ns: "gamezone",
		})
	);
	const players = [{player_id: playerId}];

	window.Trialfire.track("pick_submitted", {
		game: "allstar",
		season,
		players,
		type: "fan_vote",
	});

	window.rudderanalytics.track("pick_submitted", {
		game: "allstar",
		season,
		players,
		type: "fan_vote",
	});
};

export const saveFanVotePlayerSaga = function* (): SagaIterator {
	try {
		const isLogged = yield* select(isLoggedIn);
		const playerId = yield* select(getFanVotePlayerId);

		if (isNull(playerId)) {
			yield* put(saveFanVotePlayerFailed());
			return;
		}

		if (!isLogged) {
			localStorage.setItem(FAN_VOTE_KEY, JSON.stringify(playerId));
			yield* put(openDontLoseTeamModal());
			return;
		}

		const response = yield* call(Api.Vote.save, {player: playerId});

		ApiError.CHECK(response);

		yield* put(saveFanVotePlayerSuccess(response.success));
		yield* put(
			openFanVoteSavedModal({
				voteId: first(response.success.history)!.id,
				playerId,
			})
		);

		trackAnalytics(playerId);
	} catch (e) {
		yield* put(saveFanVotePlayerFailed());
		const error = e as ApiError;
		yield* put(toggleErrorModal(error.message));
	}
};
